import { useEffect, useRef, useState } from 'react';
import { useTouch } from './TouchControls';

// ─────────────────────────────────────────────────────────────────────────────
// The ocean's inventory (I, or 🎒): every item the student has, and the 9-slot
// hotbar along the top, arranged however they like, as in Minecraft:
//   • click an item, then a hotbar slot, to put it there (or drag it there)
//   • hover an item and press 1–9 to send it to that slot
//   • click a hotbar item to pick it up and move it; ✕ clears a slot
//   • double-click an item to use it now
// The hotbar is saved in this browser (routes/student/Ocean.tsx).
// ─────────────────────────────────────────────────────────────────────────────

export type InvItem = { key: string; icon: string; label: string; count?: number; color?: string };
export const HOTBAR_SIZE = 9;

type Held = { key: string; from: number | null };   // from: the hotbar slot it came from, or null (the grid)

type Props = {
  items: InvItem[];
  hotbar: (string | null)[];
  onHotbar: (next: (string | null)[]) => void;
  current: string;
  onUse: (key: string) => void;
  onClose: () => void;
  /** A hotbar slot to fill next (an empty slot was clicked to open this). */
  target?: number | null;
};

/** Put `key` in slot `i`. If it was already in another slot, that slot gets whatever was in `i` (a swap). */
export function place(hotbar: (string | null)[], key: string, i: number): (string | null)[] {
  const next = [...hotbar];
  const was = next.indexOf(key);
  if (was >= 0) next[was] = next[i];
  next[i] = key;
  return next;
}

function Icon({ it }: { it: InvItem }) {
  return it.color ? <span className="hot-block" style={{ background: it.color }} /> : <span aria-hidden>{it.icon}</span>;
}

export default function InventoryPanel({ items, hotbar, onHotbar, current, onUse, onClose, target = null }: Props) {
  const touch = useTouch();
  const [held, setHeld] = useState<Held | null>(null);
  const [aim, setAim] = useState<number | null>(target);
  const hover = useRef<string | null>(null);
  const byKey = new Map(items.map(i => [i.key, i]));

  // 1–9 over an item sends it to that slot; Escape (or I) closes.
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (/^[1-9]$/.test(e.key) && hover.current) {
        e.preventDefault(); e.stopPropagation();
        onHotbar(place(hotbar, hover.current, Number(e.key) - 1));
      } else if (e.key === 'Escape' || e.key.toLowerCase() === 'i') { e.preventDefault(); e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [hotbar, onHotbar, onClose]);

  const clickGrid = (key: string) => {
    if (aim != null) { onHotbar(place(hotbar, key, aim)); setAim(null); setHeld(null); return; }
    setHeld(h => (h?.key === key && h.from == null ? null : { key, from: null }));
  };
  const clickSlot = (i: number) => {
    if (held) {
      if (held.from === i) { setHeld(null); return; }
      onHotbar(place(hotbar, held.key, i));
      setHeld(null);
      return;
    }
    const k = hotbar[i];
    if (k && byKey.has(k)) setHeld({ key: k, from: i });
    else setAim(a => (a === i ? null : i));            // an empty slot: the next item clicked goes here
  };
  const drop = (i: number, e: React.DragEvent) => {
    e.preventDefault();
    const key = e.dataTransfer.getData('text/plain');
    if (key) onHotbar(place(hotbar, key, i));
    setHeld(null);
  };

  return (
    <div className="ocean-modal" role="dialog" aria-modal="true" aria-label="Inventory" onClick={onClose}>
      <div className="ocean-card tray wide inventory" onClick={e => e.stopPropagation()}>
        <div className="row"><h3>🎒 Inventory</h3><span className="small muted">{items.length} item{items.length === 1 ? '' : 's'}</span><span className="spacer" /><button type="button" className="icon" aria-label="Close" onClick={onClose}>✕</button></div>

        <div className="inv-hotbar" aria-label="Hotbar">
          {hotbar.map((k, i) => {
            const it = k ? byKey.get(k) : undefined;
            const isHeld = held?.from === i, isAim = aim === i;
            return (
              <div key={i} className={`inv-slot-wrap`}>
                <button type="button" className={`hot-slot inv-bar-slot${it && it.key === current ? ' on' : ''}${isHeld ? ' held' : ''}${isAim ? ' aim' : ''}${held && !isHeld ? ' drop' : ''}`}
                  title={it ? `${i + 1}: ${it.label}` : `${i + 1}: empty`} aria-label={it ? `Slot ${i + 1}: ${it.label}` : `Slot ${i + 1}: empty`}
                  draggable={!!it} onDragStart={e => it && e.dataTransfer.setData('text/plain', it.key)}
                  onDragOver={e => e.preventDefault()} onDrop={e => drop(i, e)}
                  onClick={() => clickSlot(i)}>
                  {it ? <Icon it={it} /> : null}
                  {it?.count != null && <span className="hot-count">{it.count}</span>}
                  <span className="hot-key">{i + 1}</span>
                </button>
                {k && <button type="button" className="inv-clear" aria-label={`Clear slot ${i + 1}`} onClick={() => { const n = [...hotbar]; n[i] = null; onHotbar(n); setHeld(null); }}>✕</button>}
              </div>
            );
          })}
        </div>
        <p className="small muted inv-help">
          {held ? <>Now {touch ? 'tap' : 'click'} a hotbar slot to put <strong>{byKey.get(held.key)?.label.split(':')[0]}</strong> there.</>
            : aim != null ? <>{touch ? 'Tap' : 'Click'} an item to put it in slot {aim + 1}.</>
              : touch ? <>Tap an item, then a hotbar slot, to put it there. Tap a hotbar item to move it; ✕ empties a slot.</>
                : <>Click an item, then a hotbar slot, to put it there (or drag it). Hover an item and press <kbd>1</kbd>–<kbd>9</kbd> to send it straight to that slot. Double-click to use it now.</>}
        </p>

        <div className="inv-grid">
          {items.map(it => {
            const slot = hotbar.indexOf(it.key);
            return (
              <button key={it.key} type="button" className={`inv-item${held?.key === it.key ? ' held' : ''}${it.key === current ? ' on' : ''}`}
                draggable onDragStart={e => e.dataTransfer.setData('text/plain', it.key)}
                onMouseEnter={() => { hover.current = it.key; }} onMouseLeave={() => { if (hover.current === it.key) hover.current = null; }}
                onClick={() => clickGrid(it.key)} onDoubleClick={() => { onUse(it.key); onClose(); }}
                title={it.label}>
                <span className="inv-icon"><Icon it={it} /></span>
                <span className="inv-name">{it.label.split(':')[0].split(' (')[0]}</span>
                {it.count != null && <span className="inv-count">× {it.count}</span>}
                {slot >= 0 && <span className="inv-on-bar" title={`On hotbar slot ${slot + 1}`}>{slot + 1}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
